import { Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import MinusSvg from '../../assets/icons/MinusSvg';
import theme from '../../assets/theme';
import { PromptDropdownOption } from '../../services/backendService/types';
import { Input } from './_Input';
import { Switch } from './_Switch';
import { TextArea } from './_TextArea';
import ExpandingButton from './ExpandingButton';

interface Props {
  title: string;
  visible: string | undefined;
  inputId: string;
  onOk?: () => void;
  onCancel?: () => void;
  setDropdownInputOptions: (options: PromptDropdownOption[] | undefined) => void;
  numberOfOptions: number | undefined;
  promptInputDropdownOptions?: PromptDropdownOption[];
}

const AdvancedModeContainer = styled.div`
  width: 100%;
`;

const convertDropdownInputOptionsToString = (
  dropdownInputOptions?: PromptDropdownOption[],
) => {
  if (dropdownInputOptions) {
    return dropdownInputOptions
      .map(option => {
        return option.displayName;
      })
      .join('\n');
  }
  return '';
};

const EditInputOptionsModal = (props: Props) => {
  const {
    title,
    visible,
    onOk,
    onCancel,
    setDropdownInputOptions,
    numberOfOptions,
    inputId,
    promptInputDropdownOptions,
  } = props;
  const { t } = useTranslation('Modal');
  const showModal = visible?.includes(inputId);
  const [textValue, setTextValue] = useState<string>();
  const [isAdvanced, setIsAdvanced] = useState<boolean>(false);

  useEffect(() => {
    /* We set the raw textvalue for the simple options state */
    setTextValue(convertDropdownInputOptionsToString(promptInputDropdownOptions));

    /* We set the options for the advanced options state */
    if (promptInputDropdownOptions && promptInputDropdownOptions?.length > 0) {
      const { displayName, value } = promptInputDropdownOptions[0];
      if (displayName === value) {
        setIsAdvanced(false);
      } else {
        setIsAdvanced(true);
      }
    }
  }, [promptInputDropdownOptions, visible]);

  const handleSimpleOptionsChanged = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextValue(e.target.value);
    if (e.target.value === '') {
      setDropdownInputOptions(undefined);
      return;
    }
    setDropdownInputOptions(
      e.target.value.split('\n').map(option => ({ displayName: option, value: option })),
    );
  };

  const handleAddEmptyOption = () => {
    setDropdownInputOptions([
      ...(promptInputDropdownOptions || []),
      {
        displayName: `R-${(promptInputDropdownOptions || []).length + 1}`,
        value: `my rubric ${(promptInputDropdownOptions || []).length + 1}`,
      },
    ]);
  };

  const handleEditOptionByLabel = (editedOption: PromptDropdownOption) => {
    setDropdownInputOptions(
      (promptInputDropdownOptions || []).map(option =>
        option.value === editedOption.value ? editedOption : option,
      ),
    );
  };

  const handleEditOptionByValue = (editedOption: PromptDropdownOption) => {
    setDropdownInputOptions(
      (promptInputDropdownOptions || []).map(option =>
        option.displayName === editedOption.displayName ? editedOption : option,
      ),
    );
  };

  const handleDeleteOption = (option: PromptDropdownOption) => {
    setDropdownInputOptions(
      (promptInputDropdownOptions || []).filter(
        existingOption => existingOption.value !== option.value,
      ),
    );
  };

  return (
    <StyledModal
      title={`${title} - options`}
      visible={showModal}
      onOk={onOk ? onOk : undefined}
      onCancel={onCancel ? onCancel : undefined}
      cancelText={t('Cancel')}
      okText={t('Save and close')}
      maskClosable={true}
    >
      {isAdvanced && (
        <AdvancedModeContainer>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <ExpandingButton
              buttonText="New option"
              handleClick={() => handleAddEmptyOption()}
            />
            <span style={{ marginLeft: 10 }}>First option is default</span>
          </div>
          <div>
            {promptInputDropdownOptions?.map((option, index) => {
              return (
                <div key={index} style={{ width: '100%', marginBottom: 20 }}>
                  <div
                    style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}
                  >
                    <ExpandingButton
                      buttonText="Delete option"
                      icon={<MinusSvg />}
                      handleClick={() => handleDeleteOption(option)}
                    />
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 150px',
                      gap: 10,
                      alignItems: 'center',
                    }}
                  >
                    <Input
                      label="Name"
                      value={option.displayName}
                      placeholder="The display name of the option"
                      onChange={v =>
                        handleEditOptionByLabel({
                          ...option,
                          displayName: v.target.value,
                        })
                      }
                    />
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        paddingBottom: 10,
                        height: '100%',
                      }}
                    >
                      <Switch
                        color={theme.colors.primary}
                        value={option.isEnterprise}
                        onChange={bool => {
                          handleEditOptionByValue({ ...option, isEnterprise: bool });
                        }}
                      />
                      <span style={{ marginLeft: 10 }}>School plan</span>
                    </div>
                  </div>
                  <TextArea
                    label="Value"
                    style={{ width: '100%' }}
                    rows={3}
                    onChange={v =>
                      handleEditOptionByValue({ ...option, value: v.target.value })
                    }
                    value={option.value}
                    placeholder="Paste a big option here. This value is put into the prompt"
                  />
                </div>
              );
            })}
          </div>
        </AdvancedModeContainer>
      )}
      {!isAdvanced && (
        <div style={{ position: 'relative' }}>
          <DefaultSpan>(First option is the default)</DefaultSpan>
          <TextArea
            label="Paste or type your options here. One option per line"
            style={{ width: '100%' }}
            rows={5}
            onChange={handleSimpleOptionsChanged}
            value={textValue}
          />
          {numberOfOptions && (
            <p>
              {numberOfOptions} option
              {numberOfOptions !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      )}
      <div
        style={{ width: '100%', display: 'flex', alignItems: 'center', marginTop: 10 }}
      >
        <Switch
          color={theme.colors.primary}
          value={isAdvanced}
          onChange={bool => {
            setIsAdvanced(bool);
          }}
        />
        <span style={{ marginLeft: 10 }}>
          {`Advanced options (${isAdvanced ? 'ON' : 'OFF'})`}
        </span>
      </div>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 0px 24px;
  }
  .ant-modal-content {
    border-radius: 30px;
    height: 100%;
  }
  .ant-modal-header {
    border-radius: 30px;
    border: 0;
  }
  .ant-modal-footer {
    border: 0;
    padding: 20px;
    & div {
      display: flex;
      justify-content: space-evenly;
    }
  }
  .ant-modal-close {
    display: none;
  }

  .edit-input-options-modal {
    position: relative;
  }
`;

const DefaultSpan = styled.span`
  position: absolute;
  font-size: 12px;
  color: #999999;
  top: 22px;
  right: 2px;
  z-index: 1;
`;

export default EditInputOptionsModal;
